import { Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { EventSubscription, EventSubscriptionRequest } from '@frontend/sports/event-subscription/feature/base-subscription';
import { DetailedEventSubscriptionService } from '@frontend/sports/event-subscription/feature/detailed-event-subscription';
import { EventSubscriptionService } from '@frontend/sports/event-subscription/feature/event-subscription';
import { debounce, entries, groupBy, includes, partition } from 'lodash-es';
import { Observable, Subject, asyncScheduler, buffer, filter, map, noop, throttleTime } from 'rxjs';

import { PlayerStatsSubscriptionService } from '../event-subscription/player-stats-subscription-service';
import { TopEventsSubscriptionService } from '../event-subscription/top-events-subscription.service';
import { PodType, TopEventUpdate, TopEventsPodData } from './top-events.model';

@Injectable({ providedIn: 'root' })
export class TopEventsPushService implements OnDestroy {
    private readonly podsWithoutGeneralPushHandling = [PodType.Grid];

    private subscriptions?: EventSubscription<EventModel>;
    private eventsStream$ = new Subject<TopEventUpdate>();

    // emits after 500ms of getting a push update all the updates that were received in that time period grouped per podId
    readonly updatedPods$: Observable<StringDictionary<EventModel[]>>;

    constructor(
        private eventSubscriptionService: EventSubscriptionService,
        private topEventsSubscriptionService: TopEventsSubscriptionService,
        private detailedEventSubscriptionService: DetailedEventSubscriptionService,
        private playerStatsSubscriptionService: PlayerStatsSubscriptionService<EventModel>,
    ) {
        const updateBufferingMilliseconds = 400;

        const eventFlush$ = this.eventsStream$.pipe(
            map(noop),
            throttleTime(updateBufferingMilliseconds, asyncScheduler, { leading: false, trailing: true }),
        );

        this.updatedPods$ = this.eventsStream$.pipe(
            buffer(eventFlush$),
            filter((updates) => updates && updates.length > 0),
            map((updates) => {
                const result: StringDictionary<EventModel[]> = {};

                entries(groupBy(updates, (update) => update.podId)).forEach((podUpdate) => (result[podUpdate[0]] = podUpdate[1].map((v) => v.event)));

                return result;
            }),
            takeUntilDestroyed(),
        );
    }

    // resubsribe to all because some events appear in multiple pods, so cant remove them based on podId
    resubscribe(pods: TopEventsPodData[]): void {
        this.unsubscribeAll();

        const [withMarketParams, withoutMarketParams] = partition(pods, (p) => !!p.marketParameters?.length || p.podType === PodType.BetBuilder);
        const [detailed, slim] = partition(
            withoutMarketParams.filter((p) => !includes(this.podsWithoutGeneralPushHandling, p.podType)),
            (p) => p.isDetailed,
        );
        const withPlayerStats: TopEventsPodData[] = pods.map((pod) => ({
            ...pod,
            events: pod.events.filter((event) => event.live && event.optionGroups.some((og) => og.detailedGrouping?.playerStatsProps?.length)),
        }));

        const toRequest = (data: TopEventsPodData[]) =>
            data.flatMap((p) =>
                p.events.map(
                    (ev) =>
                        ({
                            event: ev,
                            callback: debounce(this.handler(p.podId), 100),
                        }) as EventSubscriptionRequest<EventModel>,
                ),
            );

        this.subscriptions = this.topEventsSubscriptionService
            .subscribe(toRequest(detailed))
            .add(this.eventSubscriptionService.subscribe(toRequest(slim)));

        if (withMarketParams) {
            this.subscriptions.add(this.detailedEventSubscriptionService.subscribe(toRequest(withMarketParams)));
        }

        if (withPlayerStats) {
            this.subscriptions.add(this.playerStatsSubscriptionService.subscribe(toRequest(withPlayerStats)));
        }
    }

    private handler(podId: string): (event: EventModel) => void {
        return (event) => {
            if (event) {
                this.eventsStream$.next({ podId, event });
            }
        };
    }

    unsubscribeAll(): void {
        this.subscriptions?.unsubscribe();
    }

    ngOnDestroy(): void {
        this.unsubscribeAll();
    }
}
